import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import * as Icons from "lucide-react";
import Reveal from "@/components/Reveal";
import { FEATURES } from "@/config/site";

const TiltCard = ({ feature, index }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const Icon = Icons[feature.icon] || Icons.Circle;

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(900px) rotateY(${px * 7}deg) rotateX(${-py * 7}deg) translateY(-4px)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(900px) rotateY(0) rotateX(0) translateY(0)";
  };

  return (
    <Reveal delay={(index % 3) * 0.06}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="card-glow group relative h-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] p-6 transition-[transform] duration-200 will-change-transform"
        data-testid={`feature-card-${index}`}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-nova-cyan/20 to-nova-emerald/10 text-nova-cyan">
            <Icon size={20} />
          </div>
          {feature.premium && (
            <span className="rounded-full border border-nova-emerald/30 bg-nova-emerald/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-nova-emerald" data-testid={`feature-premium-${index}`}>
              Premium
            </span>
          )}
        </div>
        <h3 className="font-heading text-base font-semibold text-white">{feature.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-white/55">{feature.desc}</p>
      </div>
    </Reveal>
  );
};

export const Features = () => {
  return (
    <section id="features" className="relative scroll-mt-24 py-24 sm:py-32">
      <div className="container-nova">
        <Reveal>
          <p className="label-eyebrow mb-4">Features</p>
          <h2 className="max-w-2xl font-heading text-3xl font-800 leading-tight tracking-tight text-white sm:text-5xl" style={{ fontWeight: 800 }}>
            Everything you need to <span className="text-gradient-accent">understand your money</span>
          </h2>
          <p className="mt-4 max-w-2xl text-white/60">
            Real Nova features, built to make budgeting and debt payoff feel calm and clear. Items marked Premium require a Nova Premium subscription.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <TiltCard key={f.title} feature={f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
